import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";

import { updatePickupStatus } from '@features/pickups/services/pickupService';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import { Check, Settings, X } from "lucide-react";
import { useState } from "react";
import { Input } from "@shared/components/ui/input";
import { useTranslation } from "react-i18next";

const PickupAction = ({ pickupId, initialQuantity }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState(initialQuantity);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (action) => updatePickupStatus(pickupId, action),
  });

  const handleAction = async (action) => {
    const toastId = toast.loading(t("pickups.approvingPickup"));
    mutate(action, {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["pickups", pickupId] });
        toast.success(
          t("pickups.pickupActionSuccess", { action: data.response }),
          { id: toastId }
        );
        setOpen(false); // Close dialog on success
      },
      onError: (error) => {
        toast.error(`${error.response.data.message}`, { id: toastId });
      },
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button disabled={isPending} variant="ghost">
            <Settings className="size-4 text-blue-600 mx-1" />
            {t("action.actions")}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("pickup.action")}</DialogTitle>
            <DialogDescription>{t("pickup.confirmation")}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="quantity">{t("pickup.quantity")}</Label>
              <Input
                id="quantity"
                type="number"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                }}
              />
              <Label htmlFor="note" className="sr-only">
                {t("pickup.note")}
              </Label>

              <Textarea
                id="note"
                value={note}
                placeholder={t("pickup.notePlaceholder")}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
              />
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <DialogClose asChild>
              <Button type="button" size="sm" variant="secondary">
                {t("common.close")}
              </Button>
            </DialogClose>
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                className="bg-green-600 hover:bg-green-500"
                disabled={isPending}
                onClick={() =>
                  handleAction({
                    status: "approved",
                    statusNote: note,
                    quantity: parseInt(quantity),
                  })
                }
              >
                <Check className="mx-1 size-4" />
                {t("pickup.approve")}
                <span className="sr-only">{t("pickup.approve")}</span>
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  handleAction({
                    status: "rejected",
                    statusNote: note,
                  })
                }
                disabled={isPending}
                variant="destructive"
              >
                <X className="mx-1 size-4" />
                {t("pickup.reject")}
                <span className="sr-only">{t("pickup.reject")}</span>
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PickupAction;
